import * as React from "react";
import { ConnectProto } from "@racoon/api";
import {
  useCancelJobMutation,
  useDetachJobFromRouteMutation,
  useForceCompleteJobMutation,
} from "~/shared/api/jobs/job.grpc";
import {
  useCancelRouteMutation,
  useChangeRouteDriverOnShiftMutation,
  useCreateRouteWithJobsMutation,
  useForceCompleteRouteMutation,
  useLazyListRoutesQuery,
} from "~/shared/api/routes/route.grpc";
import { useForceCompleteDriverOnShiftMutation } from "~/shared/api/shift/driver-on-shift.grpc";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { tripLike } from "~/shared/lib/trip-like";
import {
  runForceComplete,
  type ForceCompleteApi,
  type RunMemory,
} from "../lib/force-complete-shift";

type RunArgs = Omit<Parameters<typeof runForceComplete>[0], "api" | "memory" | "onProgress">;

/**
 * What a failed run leaves to its retry, per shift and outliving the modal: jobs
 * it pooled must get their trip even after the dispatcher closes and reopens.
 */
const memories = new Map<string, RunMemory>();

/**
 * Runs the emergency completion of one shift with the existing route and job
 * RPCs. `progress` is set while a run is in flight; a retry after a failure
 * carries on from what the failed run left.
 */
export const useForceCompleteRun = (shiftId: bigint) => {
  const { selectedCompanyId } = useSelectedCompany();
  const ownerOrganizationId = BigInt(selectedCompanyId ?? 0);

  const [cancelJob] = useCancelJobMutation();
  const [forceCompleteJob] = useForceCompleteJobMutation();
  const [detachJob] = useDetachJobFromRouteMutation();
  const [forceCompleteRoute] = useForceCompleteRouteMutation();
  const [cancelRoute] = useCancelRouteMutation();
  const [moveRoute] = useChangeRouteDriverOnShiftMutation();
  const [createRoute] = useCreateRouteWithJobsMutation();
  const [listRoutes] = useLazyListRoutesQuery();
  const [completeShift] = useForceCompleteDriverOnShiftMutation();

  const [progress, setProgress] = React.useState<{ done: number; total: number } | null>(null);

  // Ids reach the UI as strings (the cache normalizes bigints), so each one is converted back.
  const api = React.useMemo<ForceCompleteApi>(() => {
    const job = (j: ConnectProto.Job) => ({
      id: BigInt(j.id),
      serviceProviderId: BigInt(j.serviceProviderId),
      serviceReceiverId: BigInt(j.serviceReceiverId),
    });
    const ids = (list: bigint[]) => list.map((id) => BigInt(id));
    return {
      cancelJob: (j) => cancelJob(job(j)).unwrap(),
      forceCompleteJob: (j) => forceCompleteJob(job(j)).unwrap(),
      detachJob: (j) => detachJob(job(j)).unwrap(),
      forceCompleteRoute: (id, complete, unassign) =>
        forceCompleteRoute({
          id: BigInt(id),
          jobsToComplete: ids(complete),
          jobsToUnassign: ids(unassign),
          ownerOrganizationId,
        }).unwrap(),
      cancelRoute: (id, cancel, unassign) =>
        cancelRoute({
          id: BigInt(id),
          jobsToCancel: ids(cancel),
          jobsToUnassign: ids(unassign),
          ownerOrganizationId,
        }).unwrap(),
      moveRoute: (routeId, driverOnShiftId) =>
        moveRoute({
          routeId: BigInt(routeId),
          driverOnShiftId: BigInt(driverOnShiftId),
          ownerOrganizationId,
        }).unwrap(),
      createTrip: (like, driverOnShiftId, jobIds) =>
        createRoute({
          create: tripLike(like, BigInt(driverOnShiftId), ownerOrganizationId),
          ownerOrganizationId,
          jobIds: ids(jobIds),
        }).unwrap(),
      loadRoutes: async () =>
        (
          await listRoutes({
            ownerOrganizationIds: [ownerOrganizationId],
            driverOnShiftIds: [shiftId],
          }).unwrap()
        ).routes ?? [],
      completeShift: (reason, explanation) =>
        completeShift({
          id: shiftId,
          ownerOrganizationId,
          reason,
          explanation: explanation || undefined,
        }).unwrap(),
    };
  }, [
    ownerOrganizationId,
    shiftId,
    cancelJob,
    forceCompleteJob,
    detachJob,
    forceCompleteRoute,
    cancelRoute,
    moveRoute,
    createRoute,
    listRoutes,
    completeShift,
  ]);

  const run = React.useCallback(
    async (args: RunArgs) => {
      setProgress({ done: 0, total: 0 });
      const key = String(shiftId);
      const memory = memories.get(key) ?? {};
      memories.set(key, memory);
      try {
        await runForceComplete({
          ...args,
          api,
          memory,
          onProgress: (done, total) => setProgress({ done, total }),
        });
        memories.delete(key);
      } finally {
        setProgress(null);
      }
    },
    [api, shiftId],
  );

  return { run, progress };
};
